import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { TrendingUp, Clock, Wallet, RefreshCw, DollarSign, Euro, PoundSterling, Activity, Package, ArrowDownRight, Award, Truck, BookOpenCheck, AlertTriangle, ClipboardCheck } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import api from '../utils/api';
import toast from 'react-hot-toast';

const COLORS = ['#6366f1', '#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#ec4899'];

const currencyIcons = {
    USD: <DollarSign size={24} className="text-green-400" />,
    EUR: <Euro size={24} className="text-blue-400" />,
    GBP: <PoundSterling size={24} className="text-yellow-400" />,
    TRY: <span className="text-lg font-bold text-red-400">₺</span>
};

const Dashboard = () => {
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();
    const [summary, setSummary] = useState([]);
    const [recentActivites, setRecentActivities] = useState([]);
    const [sellerStats, setSellerStats] = useState({ collections: [], shipments: [] });
    const [readyToShipCount, setReadyToShipCount] = useState(0);
    const [actionSummary, setActionSummary] = useState({ unaccounted: 0, pendingCollections: 0, overdueTasks: 0, missingProof: 0 });
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const fetchSummary = useCallback(async (silent = false) => {
        if (!silent) setIsLoading(true);
        setIsRefreshing(true);
        try {
            const now = new Date();
            const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
            const canViewTasks = ['admin', 'boss', 'staff'].includes(user?.role);
            const [summaryRes, collectionsRes, shipmentsRes, sellerStatsRes, readyRes, unaccountedRes, tasksRes] = await Promise.all([
                api.get('/collections/dashboard'),
                api.get('/collections?limit=8'),
                api.get('/shipments?limit=8'),
                api.get('/collections/seller-stats'),
                api.get('/shipments', { params: { status: 'ready', limit: 1 } }),
                api.get('/collections', { params: { accountingStatus: 0, limit: 1 } }),
                canViewTasks ? api.get('/company-tasks/monthly', { params: { month: monthKey } }) : Promise.resolve({ data: { items: [] } })
            ]);

            setSummary(summaryRes.data);
            setReadyToShipCount(readyRes.data?.pagination?.total || 0);
            setSellerStats({
                collections: sellerStatsRes.data?.collections || [],
                shipments: sellerStatsRes.data?.shipments || []
            });
            const taskItems = tasksRes.data?.items || [];
            setActionSummary({
                unaccounted: unaccountedRes.data?.pagination?.total || 0,
                pendingCollections: (summaryRes.data || []).reduce((total, item) => total + Number(item.pendingCount || 0), 0),
                overdueTasks: taskItems.filter((item) => item.isOverdue && !item.isCompleted && !item.isSkipped).length,
                missingProof: taskItems.filter((item) => item.missingProof).length,
            });

            const cols = (collectionsRes.data.data || []).map(c => ({ ...c, type: 'collection' }));
            const ships = (shipmentsRes.data.data || []).map(s => ({ ...s, type: 'shipment' }));

            const combined = [...cols, ...ships]
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                .slice(0, 10);

            setRecentActivities(combined);
        } catch (error) {
            if (!silent) toast.error('Özet veriler alınamadı.');
        } finally {
            setIsLoading(false);
            setIsRefreshing(false);
        }
    }, [user?.role]);

    useEffect(() => {
        if (user?.role === 'shipment_staff') {
            navigate('/shipments');
            return;
        }
        fetchSummary();
    }, [user?.role, navigate, fetchSummary]);

    // Para biçimlendirme
    const formatAmount = (amount) => {
        return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
    };

    // Grafik verileri
    const pieData = summary.map(s => ({
        name: s.currency,
        value: (s.totalCollected || 0) + (s.totalPending || 0),
    })).filter(d => d.value > 0);

    const barData = summary.map(s => ({
        currency: s.currency,
        'Tahsil Edilen': s.totalCollected || 0,
        'Bekleyen': s.totalPending || 0,
    }));

    if (isLoading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-full">
                    <div className="relative w-16 h-16">
                        <div className="absolute inset-0 rounded-full border-t-2 border-primary-500 animate-spin"></div>
                        <div className="absolute inset-2 rounded-full border-r-2 border-blue-500 animate-spin animate-reverse"></div>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-xl md:text-3xl font-bold text-dark-text tracking-tight mb-1">Veri Merkezi & Özet</h1>
                    <p className="text-dark-muted text-sm md:text-base">Genel finansal durum ve en son sistem aktiviteleri.</p>
                </div>
                <button
                    onClick={() => fetchSummary(true)}
                    className={`p-3 text-dark-muted hover:text-dark-text hover:bg-dark-hover rounded-xl transition-all shadow-sm border border-dark-border bg-dark-bg/50 ${isRefreshing ? 'animate-spin' : ''}`}
                    title="Yenile"
                >
                    <RefreshCw size={22} />
                </button>
            </div>

            {/* BUGÜN MÜDAHALE GEREKTİREN İŞLER */}
            <div className="mb-6">
                <div className="mb-3 flex items-center justify-between">
                    <div>
                        <h2 className="text-lg font-bold text-dark-text">Bugün neye müdahale edilmeli?</h2>
                        <p className="text-xs text-dark-muted">Kartlar ilgili filtrelenmiş iş listesine götürür.</p>
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                    <button onClick={() => navigate('/collections?accountingStatus=0')} className="glass rounded-2xl border border-blue-500/25 p-4 text-left transition-all hover:-translate-y-0.5 hover:border-blue-400/60">
                        <BookOpenCheck size={20} className="mb-3 text-blue-400" />
                        <div className="text-2xl font-bold text-dark-text">{actionSummary.unaccounted}</div>
                        <div className="text-xs font-medium text-dark-muted">Muhasebeleştirilmemiş tahsilat</div>
                    </button>
                    <button onClick={() => navigate('/collections?status=0')} className="glass rounded-2xl border border-orange-500/25 p-4 text-left transition-all hover:-translate-y-0.5 hover:border-orange-400/60">
                        <Clock size={20} className="mb-3 text-orange-400" />
                        <div className="text-2xl font-bold text-dark-text">{actionSummary.pendingCollections}</div>
                        <div className="text-xs font-medium text-dark-muted">Bekleyen tahsilat</div>
                    </button>
                    <button onClick={() => navigate('/company-tasks')} className="glass rounded-2xl border border-red-500/25 p-4 text-left transition-all hover:-translate-y-0.5 hover:border-red-400/60">
                        <AlertTriangle size={20} className="mb-3 text-red-400" />
                        <div className="text-2xl font-bold text-dark-text">{actionSummary.overdueTasks}</div>
                        <div className="text-xs font-medium text-dark-muted">Geciken idari görev</div>
                    </button>
                    <button onClick={() => navigate('/company-tasks')} className="glass rounded-2xl border border-amber-500/25 p-4 text-left transition-all hover:-translate-y-0.5 hover:border-amber-400/60">
                        <ClipboardCheck size={20} className="mb-3 text-amber-400" />
                        <div className="text-2xl font-bold text-dark-text">{actionSummary.missingProof}</div>
                        <div className="text-xs font-medium text-dark-muted">Belgesi eksik görev</div>
                    </button>
                </div>
            </div>

            {/* SEVKE HAZIR BEKLEYEN */}
            {readyToShipCount > 0 && (
                <button
                    onClick={() => navigate('/ready-to-ship')}
                    className="w-full glass p-4 md:p-5 rounded-2xl border border-amber-500/30 mb-6 flex items-center justify-between gap-4 hover:border-amber-500/60 transition-all hover:-translate-y-0.5 shadow-md text-left"
                >
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-amber-500/10 rounded-xl border border-amber-500/20">
                            <Truck size={24} className="text-amber-400" />
                        </div>
                        <div>
                            <h3 className="font-bold text-dark-text">Sevke Hazır Bekleyen</h3>
                            <p className="text-xs text-dark-muted mt-0.5">Faturası kesildi, araca yüklenmeyi bekliyor</p>
                        </div>
                    </div>
                    <span className="text-2xl font-bold text-amber-400">{readyToShipCount}</span>
                </button>
            )}

            {/* ÖZET KARTLARI */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {summary.map((currData, index) => (
                    <div key={currData.currency} className="glass p-6 rounded-2xl border border-dark-border relative overflow-hidden group hover:border-primary-500/30 transition-all hover:-translate-y-1 shadow-md">
                        {/* İkon + Başlık */}
                        <div className="flex items-center justify-between mb-5">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-dark-bg rounded-xl border border-dark-border">
                                    {currencyIcons[currData.currency] || <Wallet size={24} className="text-dark-muted" />}
                                </div>
                                <h3 className="text-lg font-bold text-dark-text">{currData.currency}</h3>
                            </div>
                            <span className="text-xs font-semibold text-dark-text bg-dark-bg/80 px-3 py-1 rounded-full border border-dark-border shadow-sm">
                                {currData.totalCount || currData.pendingCount || 0} Kayıt
                            </span>
                        </div>

                        {/* Tahsil Edilen */}
                        <div className="mb-4 flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm text-dark-muted">
                                <TrendingUp size={16} className="text-green-500" /> Tahsil Edilen
                            </div>
                            <span className="text-xl font-bold text-green-500">{formatAmount(currData.totalCollected)}</span>
                        </div>

                        {/* Bekleyen */}
                        <div className="mb-4 flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm text-dark-muted">
                                <Clock size={16} className="text-orange-500" /> Bekleyen
                            </div>
                            <span className="text-xl font-bold text-orange-500">{formatAmount(currData.totalPending)}</span>
                        </div>

                        {/* Toplam Çizgisi */}
                        <div className="mt-3 pt-3 border-t border-dark-border flex items-center justify-between">
                            <span className="text-xs text-dark-muted">Toplam</span>
                            <span className="font-bold text-dark-text text-lg">
                                {formatAmount((currData.totalCollected || 0) + (currData.totalPending || 0))}
                            </span>
                        </div>

                        {/* Dekoratif Köşe Efekti */}
                        <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-primary-500/5 rounded-full transition-transform group-hover:scale-150"></div>
                    </div>
                ))}

                {summary.length === 0 && (
                    <div className="col-span-full text-center py-16 text-dark-muted">
                        <Wallet size={48} className="mx-auto mb-4 opacity-50" />
                        <p className="text-lg font-medium">Henüz tahsilat kaydı oluşturulmamış.</p>
                        <p className="text-sm mt-1">İlk kaydınızı ekleyerek başlayın.</p>
                    </div>
                )}
            </div>

            {/* GRAFİKLER VE AKTİVİTELER */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* GRAFİKLER BOLUMU (Sol Taraf) */}
                <div className="lg:col-span-2 flex flex-col gap-6">
                    {summary.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* DAĞILIM GRAFİĞİ */}
                            <div className="glass p-6 rounded-2xl border border-dark-border shadow-md">
                                <h3 className="text-lg font-bold text-dark-text mb-4">Döviz Dağılımı</h3>
                                <ResponsiveContainer width="100%" height={250}>
                                    <PieChart>
                                        <Pie
                                            data={pieData}
                                            cx="50%"
                                            cy="50%"
                                            outerRadius={80}
                                            innerRadius={40}
                                            fill="#8884d8"
                                            paddingAngle={5}
                                            dataKey="value"
                                            stroke="none"
                                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                        >
                                            {pieData.map((_, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <RechartsTooltip
                                            contentStyle={{ background: 'rgb(var(--color-card))', border: '1px solid rgb(var(--color-border))', borderRadius: '12px', color: 'rgb(var(--color-text))', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                                            formatter={(value) => formatAmount(value)}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>

                            {/* ÇUBUK GRAFİĞİ */}
                            <div className="glass p-6 rounded-2xl border border-dark-border shadow-md">
                                <h3 className="text-lg font-bold text-dark-text mb-4">Tahsil vs Bekleyen</h3>
                                <ResponsiveContainer width="100%" height={250}>
                                    <BarChart data={barData}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-dark-border opacity-50" vertical={false} />
                                        <XAxis dataKey="currency" stroke="currentColor" className="text-dark-muted text-xs" tickLine={false} axisLine={false} />
                                        <YAxis stroke="currentColor" className="text-dark-muted text-xs" tickLine={false} axisLine={false} tickFormatter={(val) => { if (val >= 1000) return (val / 1000) + 'k'; return val; }} />
                                        <RechartsTooltip
                                            contentStyle={{ background: 'rgb(var(--color-card))', border: '1px solid rgb(var(--color-border))', borderRadius: '12px', color: 'rgb(var(--color-text))', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                                            cursor={{ fill: 'rgb(var(--color-hover))', opacity: 0.5 }}
                                            formatter={(value) => formatAmount(value)}
                                        />
                                        <Bar dataKey="Tahsil Edilen" fill="#22c55e" radius={[4, 4, 0, 0]} barSize={30} />
                                        <Bar dataKey="Bekleyen" fill="#f59e0b" radius={[4, 4, 0, 0]} barSize={30} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    )}

                    {/* SATICI BAZLI LİDER TABLOLARI */}
                    {(sellerStats.collections.length > 0 || sellerStats.shipments.length > 0) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* EN ÇOK TAHSİLAT (SATICI) */}
                            <div className="glass p-6 rounded-2xl border border-dark-border shadow-md">
                                <div className="flex items-center gap-2 mb-4">
                                    <Award className="text-green-500" size={20} />
                                    <h3 className="text-lg font-bold text-dark-text">En Çok Tahsilat (Satıcı)</h3>
                                </div>
                                {sellerStats.collections.length === 0 ? (
                                    <p className="text-sm text-dark-muted py-6 text-center">Kayıt yok.</p>
                                ) : (
                                    <div className="space-y-3">
                                        {sellerStats.collections.slice(0, 8).map((s, idx) => {
                                            const maxCount = sellerStats.collections[0]?.totalCount || 1;
                                            const widthPct = Math.max(8, Math.round((s.totalCount / maxCount) * 100));
                                            return (
                                                <div key={s.seller} className="relative">
                                                    <div className="flex items-center justify-between mb-1.5">
                                                        <div className="flex items-center gap-2 min-w-0">
                                                            <span className="text-xs font-bold text-dark-muted w-5 shrink-0">{idx + 1}.</span>
                                                            <span className="text-sm font-semibold text-dark-text truncate" title={s.seller}>{s.seller}</span>
                                                        </div>
                                                        <span className="text-xs font-semibold text-dark-text bg-dark-bg/80 px-2 py-0.5 rounded-full border border-dark-border shrink-0 ml-2">
                                                            {s.totalCount} kayıt
                                                        </span>
                                                    </div>
                                                    <div className="h-1.5 bg-dark-bg rounded-full overflow-hidden mb-1.5">
                                                        <div className="h-full bg-green-500/70 rounded-full" style={{ width: `${widthPct}%` }}></div>
                                                    </div>
                                                    <div className="flex flex-wrap gap-1.5 pl-7">
                                                        {(s.byCurrency || []).map((c) => (
                                                            <span key={c.currency} className="text-[10px] font-medium text-dark-muted bg-dark-card px-2 py-0.5 rounded border border-dark-border">
                                                                {formatAmount(c.total)} {c.currency}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* EN ÇOK SEVKİYAT (SATICI) */}
                            <div className="glass p-6 rounded-2xl border border-dark-border shadow-md">
                                <div className="flex items-center gap-2 mb-4">
                                    <Truck className="text-blue-500" size={20} />
                                    <h3 className="text-lg font-bold text-dark-text">En Çok Sevkiyat (Satıcı)</h3>
                                </div>
                                {sellerStats.shipments.length === 0 ? (
                                    <p className="text-sm text-dark-muted py-6 text-center">Kayıt yok.</p>
                                ) : (
                                    <div className="space-y-3">
                                        {sellerStats.shipments.slice(0, 8).map((s, idx) => {
                                            const maxSacks = sellerStats.shipments[0]?.totalSacks || 1;
                                            const widthPct = Math.max(8, Math.round(((s.totalSacks || 0) / maxSacks) * 100));
                                            return (
                                                <div key={s.seller} className="relative">
                                                    <div className="flex items-center justify-between mb-1.5">
                                                        <div className="flex items-center gap-2 min-w-0">
                                                            <span className="text-xs font-bold text-dark-muted w-5 shrink-0">{idx + 1}.</span>
                                                            <span className="text-sm font-semibold text-dark-text truncate" title={s.seller}>{s.seller}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2 shrink-0 ml-2">
                                                            <span className="text-sm font-bold text-blue-400">{s.totalSacks || 0}</span>
                                                            <span className="text-[10px] text-dark-muted">çuval</span>
                                                            <span className="text-[10px] font-medium text-dark-muted bg-dark-card px-2 py-0.5 rounded border border-dark-border">{s.count} sevk.</span>
                                                        </div>
                                                    </div>
                                                    <div className="h-1.5 bg-dark-bg rounded-full overflow-hidden">
                                                        <div className="h-full bg-blue-500/70 rounded-full" style={{ width: `${widthPct}%` }}></div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* SON AKTİVİTELER (Sağ Taraf) */}
                <div className="glass p-6 rounded-2xl border border-dark-border shadow-md flex flex-col h-full overflow-hidden max-h-[600px]">
                    <div className="flex items-center gap-2 mb-4 pb-4 border-b border-dark-border">
                        <Activity className="text-primary-500" size={20} />
                        <h3 className="text-lg font-bold text-dark-text">Son İşlemler Feed'i</h3>
                    </div>

                    <div className="flex-1 overflow-y-auto pr-2 space-y-3 custom-scrollbar">
                        {recentActivites.length > 0 ? (
                            recentActivites.map((activity, idx) => (
                                <div key={idx} className="p-3 bg-dark-bg/60 border border-dark-border rounded-xl hover:bg-dark-hover transition-colors flex flex-col gap-2">
                                    <div className="flex justify-between items-start">
                                        <div className="flex items-center gap-2">
                                            <div className={`p-1.5 rounded-lg ${activity.type === 'collection' ? 'bg-green-500/10 text-green-500' : 'bg-blue-500/10 text-blue-500'}`}>
                                                {activity.type === 'collection' ? <ArrowDownRight size={16} /> : <Package size={16} />}
                                            </div>
                                            <div>
                                                <p className="text-sm font-semibold text-dark-text leading-tight">{activity.seller} → {activity.buyer}</p>
                                                <p className="text-[10px] text-dark-muted capitalize mt-0.5">
                                                    {activity.type === 'collection' ? 'Tahsilat' : 'Sevkiyat'} • {new Date(activity.createdAt).toLocaleString('tr-TR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between pl-9 mt-1">
                                        {activity.type === 'collection' ? (
                                            <span className="text-xs font-bold text-dark-text bg-dark-card px-2 py-0.5 rounded border border-dark-border">
                                                {formatAmount(activity.amount)} {activity.currency}
                                            </span>
                                        ) : (
                                            <span className="text-xs font-medium text-dark-text bg-dark-card px-2 py-0.5 rounded border border-dark-border">
                                                {activity.sackCount} Çuval ({activity.route})
                                            </span>
                                        )}
                                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${activity.type === 'collection' ? (activity.isCollected ? 'bg-green-500/10 text-green-500' : 'bg-orange-500/10 text-orange-500') : 'bg-slate-500/10 text-slate-400'}`}>
                                            {activity.type === 'collection' ? (activity.isCollected ? 'Tahsil Edildi' : 'Bekliyor') : activity.city}
                                        </span>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-10 text-dark-muted text-sm">
                                Yakın zamanda işlem bulunamadı.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default Dashboard;
